import { FORM_FILE_TYPES, fileMimeType } from './formFilePolicy';

/**
 * Client-side form-resource policy — mirrors the backend `_shared/formResources.ts`
 * contract (server remains authoritative). Resources are form-level documents:
 * a safe document/image subset of the form-file allowlist (no audio/video, no
 * active formats).
 */

// Document + image subset of FORM_FILE_TYPES.
const RESOURCE_EXTENSIONS = [
    'pdf',
    'jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif',
    'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx',
    'odt', 'ods', 'odp',
    'txt', 'csv', 'rtf',
];

export const FORM_RESOURCE_TYPES = Object.fromEntries(
    RESOURCE_EXTENSIONS.filter((ext) => FORM_FILE_TYPES[ext]).map((ext) => [ext, FORM_FILE_TYPES[ext]]),
);

export const FORM_RESOURCE_ACCEPT = [...new Set(Object.values(FORM_RESOURCE_TYPES).flat())];

export const MAX_FORM_RESOURCES = 10;
export const MAX_RESOURCE_SIZE_MB = 10;

/**
 * Validate a picked resource document. Returns an error string or null.
 * `existingCount` enforces the per-form document cap.
 */
export function getFormResourceError(file, { existingCount = 0 } = {}) {
    const name = file?.filename || file?.name;
    if (typeof name !== 'string' || !name || name !== name.trim() || (/[/\\]/.test(name))) {
        return 'This file name is not allowed.';
    }
    if (existingCount >= MAX_FORM_RESOURCES) {
        return `This form already has the maximum of ${MAX_FORM_RESOURCES} documents.`;
    }
    const extension = name.split('.').pop()?.toLowerCase() || '';
    const mime = fileMimeType(file);
    if (!Object.prototype.hasOwnProperty.call(FORM_RESOURCE_TYPES, extension)
        || !FORM_RESOURCE_TYPES[extension].includes(mime)) {
        return 'This file type is not allowed. Choose a document or image file.';
    }
    if (typeof file.size === 'number' && file.size > MAX_RESOURCE_SIZE_MB * 1024 * 1024) {
        return `File exceeds ${MAX_RESOURCE_SIZE_MB}MB limit.`;
    }
    return null;
}

/**
 * Normalize/dedupe the persisted `resourcePaths` array: strings only, no empty
 * segments, no traversal, no leading slash, no signed URLs, first-wins dedupe.
 * Returns the cleaned array (does not throw — server enforces hard).
 */
export function normalizeResourcePaths(paths) {
    if (!Array.isArray(paths)) return [];
    const seen = new Set();
    const out = [];
    for (const raw of paths) {
        if (typeof raw !== 'string') continue;
        const path = raw.trim();
        if (!path || path.startsWith('/') || /^https?:\/\//i.test(path)) continue;
        if (path.split('/').some((seg) => !seg || seg === '.' || seg === '..')) continue;
        if (seen.has(path)) continue;
        seen.add(path);
        out.push(path);
        if (out.length >= MAX_FORM_RESOURCES) break;
    }
    return out;
}

/** Stable testID-friendly id for a resource row (hash of the path, never a URL). */
export function resourceStableId(path) {
    let hash = 0;
    const str = String(path || '');
    for (let i = 0; i < str.length; i++) {
        hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
    }
    return `r${(hash >>> 0).toString(36)}`;
}
